import { inject, injectable } from "inversify";
import { BankAccountInfo, ChargeApiService, ChargeCreditCardRequest, ChargeCustomerACHRequest, ChargeCustomerCreditCardRequest, ChargeCustomerStoredPaymentMethodRequest, ChargeResponse, ChargeType, CreditCard, CreditCardInfo, CreditCardIssuer, EditCreditCard, HomeApiService, Invoice, InvoicesApiService, PortalUser, SavedCreditCardsApiService, SplitInfo, StatementsApiService } from "src/api";
import { ClientDataStore } from "src/core/stores/client-data.store";
import { MembershipNameTypeStore } from "src/core/stores/membership-name-type.store";
import { RouterStore } from "src/core/stores/router.store";
import { lazyInject } from "src/ioc";
import { PGResponse, PaymentGateway, PaymentGatewayResponseType } from "src/statement/stores/payment-gateway.store";

import * as Validators from 'src/invoice/components/card-info/cc-validators';
import { FieldState, FormState } from "formstate";
import { action, computed, observable, runInAction } from "mobx";
import { AnvilSelectOptionsProps } from "@servicetitan/design-system";
import { getCardIcon, getCardNameByIssuer, getCardType, getIssuerByCardType } from "../util/credit-card-util";
import { BankAccountHolderType, ConnectedBankAccount, PaymentMethodType, PaymentMethodInfo, BankAccountType, BankAccountMethodInfo, PayByBankMethodInfo } from "src/invoice/types/types";
import { EditBankAccount } from "src/api/model/editBankAccount";
import { BankAccount } from "src/api/model/bankAccount";
import { PayByBankApiService } from "src/api/api/payByBankApi.service";
import { SavedBankAccountApiService } from "src/api/api/savedBankAccountApi.service";
import { PayByBankAccount } from "src/api/model/payByBankAccount";
import { EditPayByBankAccount } from "src/api/model/editPayByBankAccount";
import { PlaidVerificationStatus } from "src/api/model/plaidVerificationStatus";
import { AnalyticsStore } from "src/core/stores/analytics.store";
import { PaymentConfirmation } from "src/invoice/stores/invoice.store";
import { AchInfoFormState } from "src/invoice/components/ach-info/ach-info/ach-info";
import { CardInfoFormState } from "src/invoice/components/card-info/card-info/card-info";
import { accountTypesFromForm } from "src/invoice/types/helpers";

export interface PendingInvoicePayment {
    statementId?: string;
    invoiceNumber: string;
    amount: number;
    splits?: SplitInfo[];
    amountRemaining: number;
    chargeType: ChargeType;
}

@injectable()
export class SavedPaymentMethodsStore {

    private usesPaymentsServiceTokenizationStore: boolean | undefined;
    constructor(
        private savedCreditCardsApi: SavedCreditCardsApiService,
        private savedBankAccountsApi: SavedBankAccountApiService,
        private statementApiService: StatementsApiService,
        private payByBankApi: PayByBankApiService,
        private readonly paymentGatewayService: PaymentGateway,
        private readonly chargeApi: ChargeApiService,
        private readonly analytics: AnalyticsStore,
        private readonly invoicesApi: InvoicesApiService,
        private readonly homeApi: HomeApiService
    ) {
        this.usesPaymentsServiceTokenizationStore = undefined;
    }

    @lazyInject(RouterStore) router!: RouterStore;
    @lazyInject(ClientDataStore) private clientData!: ClientDataStore;
    @inject(MembershipNameTypeStore) private membershipNameTypeStore!: MembershipNameTypeStore;

    public get usesPaymentsService() {
        return this.usesPaymentsServiceTokenizationStore;
    }

    // Variables
    customerIds: number[] | undefined;

    customerInfos: PortalUser[] = [];

    postalCode = new FieldState('').validators(Validators.zipPostalCodeValidator);

    form = new FormState({ postalCode: this.postalCode });

    // Observable variables
    @observable
    onActionComplete?: (success: boolean) => void;

    @observable
    onSelectedPaymentMethodChange?: (paymentMethod: PaymentMethodInfo | undefined) => void;

    @observable
    savedPaymentMethods: PaymentMethodInfo[] | undefined;

    @observable
    isLoadingSavedPaymentMethods = true;

    @observable
    selectedPaymentMethod: PaymentMethodInfo | undefined;

    @observable
    lastSavedPaymentMethodId: number | undefined;

    @observable
    lastSavedPaymentMethod: PaymentMethodInfo | undefined;

    @observable
    lastPGResponse: PGResponse | undefined;

    @observable errorMessage?: string | any;

    @observable
    onPbbErrorMessage?: (errorMessage: string | any) => void;

    @observable processing: boolean = false;

    @observable
    needToStoreAsPrimary = false;

    @observable
    isSavedCreditCardEnabled = false;

    @observable
    isSavedCreditCardFeatureOn = false;

    @observable
    isCreditCardEnabled = false;

    @observable
    isPayByBankFeatureOn = false;

    @observable
    isPayByBankEnabled = false;

    @observable
    isSavedBankAccountFeatureOn = false;

    @observable
    isBankAccountEnabled = false;

    @observable
    isInSavedPaymentMethods = false;

    @observable
    currentInvoiceId?: string | undefined;

    @observable
    currentStatementId?: string | undefined;

    @observable
    currentSplits?: SplitInfo[] | undefined;

    @observable
    customerReference?: string | undefined;

    customerSelector = new FieldState<number | undefined>(undefined).onDidChange(({ newValue }) =>
        this.getSavedPaymentMethodsForCustomer(newValue));


    @observable
    customerOptions: AnvilSelectOptionsProps = [];

    private invoiceCache: { [id: string]: Invoice } = {};

    private paymentConfirmationCache: { [id: string]: PaymentConfirmation } = {};

    // Methods
    init = (callback?: () => void) => {
        const invoiceId = this.currentInvoiceId;
        const statementId = this.currentStatementId;

        if (statementId) {
            this.initializeWithStatement(statementId, callback);
        }
        else if (invoiceId) {
            this.initializeWithInvoice(invoiceId, callback);
        } else {
            this.homeApi.getHomeData().then((homeResponse) => {
                const homeData = homeResponse.data;
                const creditCardEnabled = !homeData?.creditCardDisabled;
                const achEnabled = !homeData?.achDisabled;
                const payByBankEnabled = !homeData?.payByBankDisabled;
                this.customerReference = homeData.customerExternalId;
                this.initialize(creditCardEnabled, payByBankEnabled, achEnabled, callback);
            }).catch(() => {
                // Handle scenario where it is a non-logged in user using their invoiceId
                if (invoiceId) {
                    this.initializeWithInvoice(invoiceId, callback);
                } else if (statementId) {
                    this.initializeWithStatement(statementId, callback);
                }
            });
        }

        this.chargeApi.paymentGatewayConfig().then((response) => {
            this.paymentGatewayService.apiKey = response.data.apiKey || '';
            this.paymentGatewayService.baseUrl = response.data.baseUrl || '';
            this.paymentGatewayService.paymentsServiceJwt = response.data.paymentsServiceJwt || '';
            this.paymentGatewayService.paymentsServiceCreditCardProcessorExists = response.data.paymentsServiceCreditCardProcessorExists || false;
        });
    };

    private initializeWithStatement(statementId: string, callback?: () => void) {
        if (!statementId) {
            if (callback) {
                callback();
            }
            return;
        }
        if (statementId) {
            this.statementApiService.getCustomerStatementsById(statementId, true).then((response) => {
                const statementData = response.data;
                const customer = statementData.customers && statementData.customers.length > 0
                    ? statementData.customers[0] : undefined;

                if (customer) {
                    let customerReference = '00000000-0000-0000-0000-000000000000';
                    // Select customer reference from available invoice
                    for (let i = 0; i < customer.invoices.length; i++) {
                        const invoice = customer.invoices[i];
                        if (invoice && invoice.customerExternalId) {
                            customerReference = invoice.customerExternalId;
                            // If not set try to retrieve via invoice API
                            if (customerReference === '00000000-0000-0000-0000-000000000000') {
                                this.getInvoice(invoice.id).then(invoiceData => {
                                    this.customerReference = invoiceData.customerExternalId || customerReference;
                                });
                            } else {
                                this.customerReference = customerReference;
                            }
                            break;
                        }
                    }

                    this.usesPaymentsServiceTokenizationStore = this.currentSplits?.some(split => split.usesPaymentsService) || false;
                    const creditCardEnabled = !customer?.creditCardDisabled;
                    const achEnabled = !customer?.achDisabled;
                    const payByBankEnabled = !customer?.payByBankDisabled;
                    this.initialize(creditCardEnabled, payByBankEnabled, achEnabled, callback);
                } else {
                    // Complete the callback to allow ui to fail if necessary
                    if (callback) {
                        callback();
                    }
                }
            });
        }
    }

    private initializeWithInvoice(invoiceId: string, callback?: () => void) {
        if (!invoiceId) {
            if (callback) {
                callback();
            }
            return;
        }
        if (invoiceId) {
            this.invoicesApi.getInvoiceById(invoiceId, true).then((response) => {
                const invoiceData = response.data;
                const creditCardEnabled = !invoiceData?.creditCardDisabled;
                const achEnabled = !invoiceData?.achDisabled;
                const payByBankEnabled = !invoiceData?.payByBankDisabled;
                this.usesPaymentsServiceTokenizationStore = response.data.usesPaymentsService;
                this.customerReference = invoiceData.customerExternalId;
                this.initialize(creditCardEnabled, payByBankEnabled, achEnabled, callback);
            });
        }
    }

    private initialize(creditCardEnabled: boolean, payByBankEnabled: boolean, achEnabled: boolean, callback: (() => void) | undefined) {
        this.savedCreditCardsApi.feature().then((response) => {
            this.isSavedCreditCardFeatureOn = response.data;
            this.isCreditCardEnabled = creditCardEnabled;

            if (this.getIsLoggedIn()) {
                this.savedCreditCardsApi.isEnabled().then((creditResponse) => {
                    let users = creditResponse.data;
                    this.isSavedCreditCardEnabled = !!users.length;
                    this.initializeFlags(payByBankEnabled, achEnabled, callback, creditCardEnabled);
                });
            } else {
                this.initializeFlags(payByBankEnabled, achEnabled, callback, creditCardEnabled);
            }
        });
    }

    private initializeFlags(payByBankEnabled: boolean, achEnabled: boolean, callback: (() => void) | undefined, creditCardEnabled: boolean) {
        this.payByBankApi.feature().then((payByBankReponse) => {
            this.isPayByBankFeatureOn = payByBankReponse.data;
            this.isPayByBankEnabled = payByBankEnabled;
            this.savedBankAccountsApi.achFeature().then((achReponse) => {
                this.isSavedBankAccountFeatureOn = achReponse.data;
                this.isBankAccountEnabled = achEnabled;
                if (callback) {
                    callback();
                }
                if ((creditCardEnabled || achEnabled || payByBankEnabled || this.isSavedCreditCardFeatureOn || this.isPayByBankFeatureOn || this.isSavedBankAccountFeatureOn)
                    && this.clientData.isLoggedIn) {
                    this.getCustomerIds();
                }
            });
        });
    }

    getIsLoggedIn = () => {
        return this.clientData.isLoggedIn;
    };

    getShowLoginLink = () => {
        return this.getIsCPEnabled() && !this.getIsLoggedIn();
    };

    getIsCPEnabled = () => {
        return this.isSavedBankAccountFeatureOn || this.isSavedCreditCardFeatureOn || this.isPayByBankFeatureOn;
    };

    getIsSavedPaymentOptionEnabled = () => {
        return this.getIsLoggedIn() && (this.isSavedCreditCardEnabled || this.isSavedBankAccountFeatureOn || this.isPayByBankFeatureOn);
    }

    getCardIssuer = (issuer: CreditCardIssuer) => {
        switch (issuer) {
            case CreditCardIssuer.NUMBER_3:
                return 'AMEX';
            case CreditCardIssuer.NUMBER_4:
                return 'VISA';
            case CreditCardIssuer.NUMBER_5:
                return 'MC';
            case CreditCardIssuer.NUMBER_6:
                return 'Discover';
            default:
                return 'Card';
        }
    };

    isExpiringSoon = (year: number, month: number) => {
        const today = new Date();
        const currentYear = today.getFullYear();
        const currentMonth = today.getMonth() + 1;
        if (currentYear === year) {
            if (currentMonth === month || currentMonth + 1 === month) {
                return true;
            }
        } else if (currentYear + 1 === year && currentMonth === 12 && month === 1) {
            return true;
        }
        return false;
    };

    isPaymentMethodExpiringSoon = (paymentMethod: PaymentMethodInfo) => {
        if (paymentMethod.paymentMethodType !== PaymentMethodType.CreditCard || !paymentMethod.expires) {
            return false;
        }

        const token = paymentMethod.expires?.split('/');
        const month = parseInt(token[0]);
        const year = parseInt(token[1]);

        return this.isExpiringSoon(year, month);
    };

    convertToCardIssuer = (name: string | undefined): CreditCardIssuer => {
        if (name === undefined) {
            return CreditCardIssuer.NUMBER_0;
        }
        switch (name.toLowerCase()) {
            case 'americanexpress':
                return CreditCardIssuer.NUMBER_3;
            case 'visa':
                return CreditCardIssuer.NUMBER_4;
            case 'mastercard':
                return CreditCardIssuer.NUMBER_5;
            case 'discover':
                return CreditCardIssuer.NUMBER_6;
            default:
                return CreditCardIssuer.NUMBER_0;
        }
    };

    getLastSavedPaymentMethod = () => {
        const result = this.lastSavedPaymentMethod;
        this.resetLastSavedPaymentMethod();
        return result;
    };

    getLastPGResponse = () => {
        const result = this.lastPGResponse;
        this.lastPGResponse = undefined;
        return result;
    }

    private resetLastSavedPaymentMethod = () => {
        this.lastSavedPaymentMethod = undefined;
        this.lastSavedPaymentMethodId = undefined;
    };

    connectedBankAccountToPaymentMethod = (connectedAccount: ConnectedBankAccount, primary: boolean) => {
        const payByBankPaymentMethod: PaymentMethodInfo = {
            paymentMethodId: 0,
            primary: primary,
            displayText: `${connectedAccount.accountName} Ending in *${connectedAccount.accountMask}`,
            name: connectedAccount.accountName,
            selected: true,
            icon: connectedAccount.bankIcon,
            bankName: connectedAccount.bankName,
            number: connectedAccount.accountMask,
            token: connectedAccount.publicToken,
            accountId: connectedAccount.accountId,
            bankId: connectedAccount.bankId,
            paymentMethodType: PaymentMethodType.PayByBank,
            verificationStatus: connectedAccount.verificationStatus,
        };

        return payByBankPaymentMethod;
    };

    cardInfoToPaymentMethod = async (cardInfo: CardInfoFormState, primary: boolean) => {
        const cardResult = await cardInfo.form.validate();
        if (cardResult.hasError) {
            return undefined;
        }

        const cardDetail = cardResult.value;
        const cardNumber = cardDetail.ccNumber.value;
        const cardName = getCardType(cardNumber);
        const customerName = cardDetail.ccName.value;
        const cardNumberEnding = cardNumber.substring(cardNumber.length - 4, cardNumber.length);
        const paymentMethod: PaymentMethodInfo = {
            paymentMethodId: 0,
            primary: primary,
            displayText: `${cardName} ending in *${cardNumberEnding}`,
            name: cardName,
            selected: true,
            icon: getCardIcon(cardName),
            expires: cardDetail.ccExp.value,
            customerName: customerName,
            bankName: cardName,
            number: cardNumber,
            paymentMethodType: PaymentMethodType.CreditCard,
        };

        return paymentMethod;
    };

    achInfoToPaymentMethod = async (achInfo: AchInfoFormState, primary: boolean) => {
        const achResult = await achInfo.form.validate();
        if (achResult.hasError) {
            return undefined;
        }

        const achDetail = achResult.value;
        const achNumber = achDetail.accountNumber.value;
        const achNumberEnding = achNumber.substring(achNumber.length - 4, achNumber.length);
        
        const [accountType, accountHolderType, bankAccountType] = accountTypesFromForm(achDetail);

        const achPaymentMethod: PaymentMethodInfo = {
            paymentMethodId: 0,
            primary: primary,
            name: 'Bank Account',
            selected: true,
            icon: 'assets/images/ach-account.svg',
            displayText: `Account Number Ending in *${achNumberEnding}`,
            number: achNumber,
            customerName: achDetail.accountName.value,
            routingNumber: achDetail.routingNumber.value,
            paymentMethodType: PaymentMethodType.ACH,
            bankAccountType,
            accountType,
            accountHolderType,
        };

        return achPaymentMethod;
    };

    creditCardToSavedPayment = (creditCard: CreditCard): PaymentMethodInfo => {
        const cardName = getCardNameByIssuer(creditCard.issuer);
        return {
            paymentMethodId: creditCard.id,
            primary: creditCard.primary,
            displayText: `${cardName} ending in *${creditCard.number}`,
            name: cardName,
            selected: creditCard.primary,
            icon: getCardIcon(cardName),
            expires: `${creditCard?.expMonth}/${creditCard?.expYear}`,
            customerName: creditCard.cardholderName,
            bankName: cardName,
            number: creditCard.number,
            paymentMethodType: PaymentMethodType.CreditCard,
            customerId: creditCard.customerId,
            membership: creditCard.membership,
        }
    };

    bankAccountToSavedPayment = (bankAccount: BankAccount): PaymentMethodInfo => {
        const routing = bankAccount.routing;
        const routingEnding = routing && routing.length >= 4 ? routing.substring(routing.length - 4, routing.length) : routing;
        return {
            paymentMethodId: bankAccount.id,
            primary: bankAccount.primary,
            displayText: `Bank Account ending in *${bankAccount.number}`,
            name: 'Bank Account',
            selected: bankAccount.primary,
            icon: 'assets/images/ach-account.svg',
            customerName: bankAccount.customerName,
            bankName: 'Bank Account',
            routingNumber: routingEnding ? `Ending in *${routingEnding}` : routingEnding,
            number: bankAccount.number,
            paymentMethodType: PaymentMethodType.ACH,
            customerId: bankAccount.customerId,
            membership: bankAccount.membership,
            accountType: bankAccount.accountType,
            accountHolderType: bankAccount.accountHolderType,
        }
    };

    payByBankToSavedPayment = async (bankAccount: BankAccount, payByBankAccount: PayByBankAccount) : Promise<PaymentMethodInfo> => {
        const institution = (await this.payByBankApi.getInstitution(payByBankAccount.pbbInstitutionId!)).data;
        const bankIcon = institution.logo ? `data:image/png;base64,${institution.logo}` : 'assets/images/ach-account.svg';
        return {
            paymentMethodId: bankAccount.id,
            primary: bankAccount.primary,
            displayText: `${payByBankAccount.accountName} Ending in *${bankAccount.number}`,
            name: payByBankAccount.accountName || '',
            selected: bankAccount.primary,
            icon: bankIcon,
            bankName: institution.name,
            number: bankAccount.number,
            customerId: bankAccount.customerId,
            membership: bankAccount.membership,
            paymentMethodType: PaymentMethodType.PayByBank,
            accountName: payByBankAccount.accountName,
            verificationStatus: this.verificationStatusEnumToString(payByBankAccount.verificationStatus),
            disconnected: (payByBankAccount.verificationStatus !== PlaidVerificationStatus.NUMBER_1 &&
                payByBankAccount.verificationStatus !== PlaidVerificationStatus.NUMBER_4)
        };
    };

    private verificationStatusEnumToString = (verificationStatus?: PlaidVerificationStatus | undefined) => {
        return verificationStatus === PlaidVerificationStatus.NUMBER_1 ? 'AutomaticallyVerified' :
            verificationStatus === PlaidVerificationStatus.NUMBER_2 ? 'PendingAutomaticVerification' :
                verificationStatus === PlaidVerificationStatus.NUMBER_3 ? 'PendingManualVerification' :
                    verificationStatus === PlaidVerificationStatus.NUMBER_4 ? 'ManuallyVerified' :
                        verificationStatus === PlaidVerificationStatus.NUMBER_5 ? 'VerificationExpired' :
                            verificationStatus === PlaidVerificationStatus.NUMBER_6 ? 'VerificationFailed' :
                                verificationStatus === PlaidVerificationStatus.NUMBER_7 ? 'DatabaseMatched' :
                                    verificationStatus === PlaidVerificationStatus.NUMBER_8 ? 'DatabaseInsightsPass' :
                                        verificationStatus === PlaidVerificationStatus.NUMBER_9 ? 'DatabaseInsightsPass' :
                                            verificationStatus === PlaidVerificationStatus.NUMBER_10 ? 'DatabaseInsightsFail' :
                                                'Unknown';
    };

    private verificationStatusToEnum = (verificationStatus?: string | undefined) => {
        return verificationStatus === 'AutomaticallyVerified' ? PlaidVerificationStatus.NUMBER_1 :
            verificationStatus === 'PendingAutomaticVerification' ? PlaidVerificationStatus.NUMBER_2 :
                verificationStatus === 'PendingManualVerification' ? PlaidVerificationStatus.NUMBER_3 :
                    verificationStatus === 'ManuallyVerified' ? PlaidVerificationStatus.NUMBER_4 :
                        verificationStatus === 'VerificationExpired' ? PlaidVerificationStatus.NUMBER_5 :
                            verificationStatus === 'VerificationFailed' ? PlaidVerificationStatus.NUMBER_6 :
                                verificationStatus === 'DatabaseMatched' ? PlaidVerificationStatus.NUMBER_7 :
                                    verificationStatus === 'DatabaseInsightsPass' ? PlaidVerificationStatus.NUMBER_8 :
                                        verificationStatus === 'DatabaseInsightsPass' ? PlaidVerificationStatus.NUMBER_9 :
                                            verificationStatus === 'DatabaseInsightsFail' ? PlaidVerificationStatus.NUMBER_10 :
                                                PlaidVerificationStatus.NUMBER_0;
    };

    sortCreditCard = (cards: CreditCard[]) => {
        if (cards.length === 0) {
            return [];
        }
        const primaryCards = cards.filter(c => c.primary && !c.isExpired);
        const membershipCards = cards.filter(c => !c.primary && c.membership && !c.isExpired);
        const others = cards
            .filter(c => c.isExpired || (!c.primary && !c.membership))
            .sort((a, b) => b.expYear + b.expMonth - (a.expYear + a.expMonth));
        return [...primaryCards, ...membershipCards, ...others];
    };

    gotoSavedCardPage = () => {
        this.router.history.push('/savedpaymentmethod/paymentmethod');
    };

    gotoMembershipPage = () => {
        this.router.history.push('/memberships');
    }

    usePaymentGateway = () => {
        return this.paymentGatewayService && !!this.paymentGatewayService.baseUrl;
    };

    getCustomerId = () => {
        return this.customerIds && this.customerIds.length > 0 ?
            this.customerIds[0] : 0;
    };

    isExpired = (paymentMethod: PaymentMethodInfo): boolean => {
        if (paymentMethod.paymentMethodType !== PaymentMethodType.CreditCard || !paymentMethod.expires) {
            return false;
        }

        const today = new Date();
        const currentYear = today.getFullYear();
        const currentMonth = today.getMonth() + 1;

        const token = paymentMethod.expires?.split('/');
        const month = parseInt(token[0]);
        const year = parseInt(token[1]);

        if (currentYear > year) {
            return true;
        }

        if (currentYear === year && currentMonth > month) {
            return true;
        }

        return false;
    };

    private callCompleteAction = (success: boolean) => {
        if (this.onActionComplete) {
            this.onActionComplete(success);
        }

        this.onActionComplete = undefined;
    };

    @action
    private callPostError = (errorMessage: string | any) => {
        if (this.onPbbErrorMessage) {
            this.onPbbErrorMessage(errorMessage);
        }
    }

    // Action Methods

    @action
    getLinkToken = async () => {
        if (!this.isPayByBankEnabled && !this.isPayByBankFeatureOn) {
            return '';
        }
        const response = (await this.payByBankApi.getLinkToken(this.customerSelector.value || new Date().getTime())).data;
        return response.token;
    }

    @action
    getInstitution = async (institutionId: string) => {
        const response = (await this.payByBankApi.getInstitution(institutionId)).data;
        if (!response || !response.logo) {
            return response.logo;
        }
        return `data:image/png;base64,${response.logo}`;
    }

    @action
    getSavedPaymentMethodsForCustomer = async (id?: number) => {
        this.isLoadingSavedPaymentMethods = true;
        if (!id) {
            this.isLoadingSavedPaymentMethods = false;
            return;
        }

        const allCards = this.isSavedCreditCardFeatureOn && (this.isInSavedPaymentMethods || this.isCreditCardEnabled) ? (await this.savedCreditCardsApi.savedCreditCards(id)).data : [];
        const allBankAccounts = (this.isSavedBankAccountFeatureOn && (this.isInSavedPaymentMethods || this.isBankAccountEnabled)) || (this.isPayByBankFeatureOn && (this.isInSavedPaymentMethods || this.isPayByBankEnabled)) ? (await this.savedBankAccountsApi.savedBankAccounts(id)).data : [];
        const allPayByBanks = (this.isPayByBankFeatureOn && (this.isInSavedPaymentMethods || this.isPayByBankEnabled)) ? (await this.payByBankApi.savedPayByBanks(id, this.customerReference || ''/*Should never be null*/)).data : [];

        await runInAction(async () => {
            const creditCards = this.sortCreditCard(allCards).map(creditCard => this.creditCardToSavedPayment(creditCard));

            const achAccounts: BankAccount[] = [];
            const payByBankAccounts: BankAccount[] = [];
            const payByBankPaymentIdMapping =
                new Map(allPayByBanks.map(pbb => [pbb.paymentsServicePaymentMethodId, pbb] as const));

            allBankAccounts.forEach(bankAccount => {
                if (payByBankPaymentIdMapping.has(bankAccount.paymentsServicePaymentMethodId)) {
                    if (this.isPayByBankFeatureOn && (this.isInSavedPaymentMethods || this.isPayByBankEnabled)) {
                        payByBankAccounts.push(bankAccount);
                    }
                } else {
                    if (this.isSavedBankAccountFeatureOn && (this.isInSavedPaymentMethods || this.isBankAccountEnabled)) {
                        //TODO: Figure a better way to distinguish between paybybank vs regular ach
                        if (!bankAccount.paymentsServicePaymentMethodId) {
                            achAccounts.push(bankAccount);
                        }
                    }
                }
            });

            const achs = achAccounts.map(ach => this.bankAccountToSavedPayment(ach));
            const payByBanks: PaymentMethodInfo[] = [];

            for (let i = 0; i < payByBankAccounts.length; i++) {
                const payByBankAccount = payByBankAccounts[i];
                const payByBankPaymentMethod = await this.payByBankToSavedPayment(payByBankAccount,
                    payByBankPaymentIdMapping.get(payByBankAccount.paymentsServicePaymentMethodId)!
                );
                payByBanks.push(payByBankPaymentMethod);
            }

            const paymentMethods = [...payByBanks, ...creditCards, ...achs];
            const primaryPaymentMethods = paymentMethods.filter(paymentMethod => paymentMethod.primary);
            const otherPaymentMethods = paymentMethods.filter(paymentMethod => !paymentMethod.primary);

            // Add payment methods
            this.savedPaymentMethods = [...primaryPaymentMethods, ...otherPaymentMethods];

            if (this.lastSavedPaymentMethodId) {
                this.lastSavedPaymentMethod = this.savedPaymentMethods.find(p => p.paymentMethodId === this.lastSavedPaymentMethodId);
            }

            this.isLoadingSavedPaymentMethods = false;

            this.setDefaultSelectedPaymentMethodToPay();
        });
    };

    @action
    getCustomerIds = async () => {
        const response = await this.savedCreditCardsApi.isEnabled();
        let users = response.data;
        this.isSavedCreditCardEnabled = !!users.length;

        if (!this.isSavedCreditCardEnabled) {
            users = (await this.homeApi.getHomeData()).data.users || [];
        }

        runInAction(() => {
            this.customerIds = users.map(i => i.id);
            this.customerInfos = users.map(i => ({ id: i.id, name: i.name }));
            this.customerOptions = this.customerInfos.map(i => ({ value: i.id, text: i.name }));
            if (this.customerIds.length) {
                this.customerSelector.onChange(this.customerIds[0]);
            }
        });
    };

    @action
    setSelectedPaymentMethodToPay = (selectedPaymentMethod: PaymentMethodInfo | undefined) => {
        this.selectedPaymentMethod = selectedPaymentMethod;
    };

    @action
    setDefaultSelectedPaymentMethodToPay = () => {
        if (!this.savedPaymentMethods || this.savedPaymentMethods.length === 0) {
            if (this.onSelectedPaymentMethodChange) {
                this.onSelectedPaymentMethodChange(undefined);
            }
            return;
        }

        const availablePaymentMethods = this.savedPaymentMethods.filter(paymentMethod => !this.isExpired(paymentMethod));
        this.selectedPaymentMethod =
            availablePaymentMethods.length > 0
                ? availablePaymentMethods.find(c => c.primary) ||
                availablePaymentMethods[0]
                : undefined;

        if (this.onSelectedPaymentMethodChange) {
            this.onSelectedPaymentMethodChange(this.lastSavedPaymentMethod || this.selectedPaymentMethod);
        }
    };

    @action
    setPrimary = (value: boolean) => (this.needToStoreAsPrimary = value);

    @action
    setOnActionComplete = (value: (success: boolean) => void) => (this.onActionComplete = value);

    @action
    setOnPbbErrorMessage = (value: (errorMessage: string | any) => void) => (this.onPbbErrorMessage = value);

    @action
    setOnSelectedPaymentMethodChange = (value: (paymentMethod: PaymentMethodInfo | undefined) => void) => (this.onSelectedPaymentMethodChange = value);

    @action
    setPaymentMethodAsPrimary = async (paymentMethod: PaymentMethodInfo) => {
        if (paymentMethod.paymentMethodType === PaymentMethodType.CreditCard) {
            await this.savedCreditCardsApi.assignCreditCard(
                paymentMethod.customerId!,
                paymentMethod.paymentMethodId,
                true);
        } else if (paymentMethod.paymentMethodType === PaymentMethodType.ACH) {
            await this.savedBankAccountsApi.assignBankAccount(paymentMethod.customerId!, paymentMethod.paymentMethodId, {
                primary: true,
                membership: paymentMethod.membership || false
            });
        } else if (paymentMethod.paymentMethodType === PaymentMethodType.PayByBank) {
            await this.payByBankApi.assignPayByBank(paymentMethod.customerId!, paymentMethod.paymentMethodId, {
                primary: true,
                membership: paymentMethod.membership || false
            });
        }

        await this.getSavedPaymentMethodsForCustomer(this.customerSelector.value);
        this.callCompleteAction(true);
    };

    @action
    deletePaymentMethod = async (paymentMethod: PaymentMethodInfo) => {
        this.processing = true;
        this.resetErrorMessage();
        try {
            if (paymentMethod.paymentMethodType === PaymentMethodType.CreditCard) {
                await this.savedCreditCardsApi.deleteCreditCard(
                    paymentMethod.customerId!,
                    paymentMethod.paymentMethodId
                );
            } else if (paymentMethod.paymentMethodType === PaymentMethodType.ACH) {
                await this.savedBankAccountsApi.deleteBankAccount(paymentMethod.customerId!, paymentMethod.paymentMethodId);
            } else if (paymentMethod.paymentMethodType === PaymentMethodType.PayByBank) {
                await this.payByBankApi.deletePayByBank(paymentMethod.customerId!, paymentMethod.paymentMethodId);
            }
        } catch (e) {
            runInAction(() => {
                this.errorMessage = e;
                this.callCompleteAction(false);
            });
        } finally {
            runInAction(() => {
                this.processing = false;
            });

            await this.getSavedPaymentMethodsForCustomer(this.customerSelector.value);
            this.callCompleteAction(true);
        }
    };

    @action
    addPaymentMethod = async (paymentMethod: PaymentMethodInfo) => {
        this.resetErrorMessage();
        this.processing = true;
        if (paymentMethod.paymentMethodType === PaymentMethodType.CreditCard) {
            if (this.usePaymentGateway()) {
                await this.form.validate();
                this.paymentGatewayService.handleSubmit(this.processAddNewCard);
            } else {
                this.processNonGatewayCreditCard(paymentMethod);
            }
        } else if (paymentMethod.paymentMethodType === PaymentMethodType.ACH) {
            await this.processACH(paymentMethod);
        } else if (paymentMethod.paymentMethodType === PaymentMethodType.PayByBank) {
            await this.processPayByBank(paymentMethod);
        }
    };

    @action
    processNonGatewayCreditCard = async (paymentMethod: PaymentMethodInfo) => {
        const selectedCustomerId = this.customerSelector.value || 0;
        const expToken = paymentMethod.expires!.split('/');
        const cardInfo: EditCreditCard = {
            id: 0,
            customerId: selectedCustomerId,
            primary: this.needToStoreAsPrimary,
            membership: true,
            businessUnitId: 0,
            isExpired: false,
            issuer: getIssuerByCardType(paymentMethod.bankName),
            expYear: parseInt(expToken[1], 10),
            expMonth: parseInt(expToken[0], 10),
            cvv: paymentMethod.cvv,
            number: paymentMethod.number,
            cardholderName: paymentMethod.customerName,
            zip: paymentMethod.postalCode,
            token: paymentMethod.token,
        };

        await this.processCreditCard(cardInfo);
    }

    @action
    processPayByBank = async (paymentMethod: PaymentMethodInfo) => {
        const selectedCustomerId = this.customerSelector.value!;
        const selectedCustomer = this.customerInfos.find(c => c.id === selectedCustomerId);
        try {
            const payByBankAccount: EditPayByBankAccount = {
                id: 0,
                customerId: selectedCustomerId,
                primary: this.needToStoreAsPrimary,
                membership: true,
                businessUnitId: 0,
                number: paymentMethod.number,
                routing: paymentMethod.routingNumber,
                bankName: paymentMethod.bankName,
                accountName: paymentMethod.name,
                customerReference: this.customerReference,
                customerName: paymentMethod.customerName || selectedCustomer?.name,
                publicToken: paymentMethod.token,
                pbbAccountId: paymentMethod.accountId,
                pbbInstitutionId: paymentMethod.bankId,
                verificationStatus: this.verificationStatusToEnum(paymentMethod.verificationStatus)
            };

            const result = await this.payByBankApi.savePayByBank(selectedCustomerId, payByBankAccount);
            this.lastSavedPaymentMethodId = result.data.id;
            await this.getSavedPaymentMethodsForCustomer(selectedCustomerId);
        } catch (e) {
            runInAction(() => {
                this.errorMessage = e;
                this.callCompleteAction(false);
            });
        } finally {
            runInAction(() => {
                this.processing = false;
                this.callCompleteAction(true);
            });
        }
    };

    @action
    chargePaymentMethod = async (paymentMethod: PaymentMethodInfo, payment: PendingInvoicePayment) => {
        this.resetErrorMessage();

        if (!payment.invoiceNumber && payment.splits && payment.splits.length > 0) {
            // Pick first invoice in splits as invoice Id to handle scenario where statement is been paid
            payment.invoiceNumber = payment.splits[0].invoiceId!;
        }

        const invoice = await this.getInvoice(payment.invoiceNumber);
        const splits: SplitInfo[] = [
            {
                invoiceId: invoice.id,
                amountApplied: payment.amount,
                usesPaymentsService: invoice.usesPaymentsService
            }
        ];

        // Only replace split if not statement
        payment.splits = payment.splits || splits;

        // Retrieve invoice number for each invoice in split
        for (let i = 0; i < payment.splits.length; i++) {
            const paymentInvoiceId = payment.splits[i].invoiceId;
            if (!paymentInvoiceId) {
                continue;
            }

            const paymentInvoice = await this.getInvoice(paymentInvoiceId);
            payment.splits[i].invoiceNumber = paymentInvoice.number;
        }

        if (paymentMethod.paymentMethodType === PaymentMethodType.ACH) {
            await this.processBankAccountCharge(paymentMethod, payment, invoice);
        } else if (paymentMethod.paymentMethodType === PaymentMethodType.CreditCard) {
            await this.processCreditCardCharge(paymentMethod, payment, invoice);
        } else if (paymentMethod.paymentMethodType === PaymentMethodType.PayByBank) {
            await this.processPayByBankCharge(paymentMethod, payment, invoice);
        }
    };

    @action
    getInvoicesById = async (invoiceIds: string[]) => {
        const result: Invoice[] = [];

        // TODO: Create API to retrieve invoices by Ids in one call (the statement api already does this)
        for (let i = 0; i < invoiceIds.length; i++) {
            const invoiceId = invoiceIds[i];
            const invoice = (await this.invoicesApi.getInvoiceById(invoiceId)).data;
            result.push(invoice);
        }

        return result;
    }

    @action
    getInvoiceByStatement = async (statementId: string) => {
        const statementData = (await this.statementApiService.getCustomerStatementsById(statementId, true)).data;
        const customer = statementData.customers && statementData.customers.length > 0
            ? statementData.customers[0] : undefined;

        if (customer) {
            for (let i = 0; i < customer.invoices.length; i++) {
                const invoice = customer.invoices[i];
                if (invoice) {
                    return invoice;
                }
            }
        }

        return null;
    };

    @action
    getInvoice = async (id: string, forceRefresh?: boolean): Promise<Invoice> => {
        const trimmedId = id.trim();

        if (!forceRefresh) {
            const cachedInvoice = this.invoiceCache[trimmedId];
            if (cachedInvoice) {
                return Promise.resolve(cachedInvoice);
            }
        }

        return this.invoicesApi.getInvoiceById(trimmedId, true).then(
            (resp) => {
                if (
                    resp.data.creditCardDisabled &&
                    resp.data.achDisabled &&
                    resp.data.payByBankDisabled &&
                    this.clientData.isAnonymousPayments
                ) {
                    this.router.history.replace('/invoice/expired-link');
                    return new Promise<never>(() => { });
                }

                this.invoiceCache[trimmedId] = resp.data;
                return Promise.resolve(resp.data);
            },
            (err) => {
                if (this.clientData.isAnonymousPayments) {
                    this.router.history.replace('/invoice/expired-link');
                    return new Promise<never>(() => { });
                }

                throw err;
            }
        );
    };

    @action
    private processBankAccountCharge = async (paymentMethod: BankAccountMethodInfo, payment: PendingInvoicePayment, invoice: Invoice) => {
        this.processing = true;
        const amount = payment.amount;

        const invoiceId = payment.chargeType === ChargeType.NUMBER_0 ? invoice.id : undefined;

        if (this.clientData.isLoggedIn && paymentMethod.paymentMethodId) {
            const request: ChargeCustomerStoredPaymentMethodRequest = {
                method: { id: paymentMethod.paymentMethodId },
                amount: amount,
                invoice: invoiceId,
                notificationEmail: this.clientData.loginData.email,
                splits: payment.splits,
                chargeType: payment.chargeType,
                storePaymentMethod: false,
                isCreditCard: false
            };
            try {
                const response = await this.chargeApi.chargeStoredPaymentMethod(request, true, {
                    400: 'Failed to charge bank account. Verify the information you’ve entered or try a different bank account.',
                });
                this.processChargeResponse(invoice, payment, response.data);
            }
            catch (e) {
                const errorMessage = e as string;
                this.callPostError(errorMessage);
                this.processChargeFailure(errorMessage, invoice, payment)
            }
            finally {
                runInAction(() => {
                    this.processing = false;
                });
            }
        } else {
            const bankAccount: BankAccountInfo = {
                name: paymentMethod.customerName!,
                routing: (paymentMethod.routingNumber || '').replace(/[^0-9]/g, ''),
                number: (paymentMethod.number || '').replace(/[^0-9]/g, ''),
                checkNumber: '',
                accountType: paymentMethod.accountType,
                accountHolderType: paymentMethod.accountHolderType,
            };

            const request: ChargeCustomerACHRequest = {
                isBusinessPayment: paymentMethod.bankAccountType === BankAccountHolderType.Business,
                chargeType: payment.chargeType,
                method: bankAccount,
                amount: amount,
                invoice: invoiceId,
                notificationEmail: this.clientData.loginData.email,
                splits: payment.splits,
                storePaymentMethod: false
            };

            try {
                const response = await this.chargeApi.chargeBankAccount(request, true, {
                    400: 'Failed to charge bank account. Verify the information you’ve entered or try a different bank account.',
                });
                this.processChargeResponse(invoice, payment, response.data);
            } catch (e) {
                const errorMessage = e as string;
                this.callPostError(errorMessage);
                this.processChargeFailure(errorMessage, invoice, payment)
            } finally {
                runInAction(() => {
                    this.processing = false;
                });
            }
        }
    }

    @action
    private processCreditCardCharge = async (paymentMethod: PaymentMethodInfo, payment: PendingInvoicePayment, invoice: Invoice) => {
        this.processing = true;
        const amount = payment.amount;
        const invoiceId = payment.chargeType === ChargeType.NUMBER_0 ? invoice.id : undefined;

        if (this.clientData.isLoggedIn && paymentMethod.paymentMethodId) {
            const request: ChargeCustomerStoredPaymentMethodRequest = {
                method: { id: paymentMethod.paymentMethodId, cvc: paymentMethod.cvv },
                amount: amount,
                invoice: invoiceId,
                notificationEmail: this.clientData.loginData.email,
                splits: payment.splits,
                chargeType: payment.chargeType,
                storePaymentMethod: false,
                isCreditCard: true
            };
            try {
                const response = await this.chargeApi.chargeStoredPaymentMethod(request, true, {
                    400: 'Failed to charge card. Verify the information you’ve entered or try a different card.',
                    500: 'Insufficient funds'
                });
                this.processChargeResponse(invoice, payment, response.data);
            }
            catch (e) {
                const errorMessage = e as string;
                this.callPostError(errorMessage);
                this.processChargeFailure(errorMessage, invoice, payment)
            }
            finally {
                runInAction(() => {
                    this.processing = false;
                });
            }
        } else {
            const lastPGResponse = this.getLastPGResponse();
            if (lastPGResponse) {
                // Used for handling scenario where iframe is closed after selection
                await this.processCharge(lastPGResponse, payment, invoice, paymentMethod.storePaymentMethod || false);
            } else {
                this.paymentGatewayService.handleSubmit(async (pgResponse: PGResponse) => {
                    await this.processCharge(pgResponse, payment, invoice, paymentMethod.storePaymentMethod || false);
                });
            }
        }
    };

    @action
    processCharge = async (pgResponse: PGResponse, payment: PendingInvoicePayment, invoice: Invoice, storePaymentMethod: boolean) => {
        if (pgResponse.Type === PaymentGatewayResponseType.Error) {
            this.processing = false;
            return;
        }

        const invoiceId = payment.chargeType === ChargeType.NUMBER_0 ? invoice.id : undefined;

        const responseData = pgResponse.Data;
        const card: CreditCardInfo = {
            token: responseData.Token,
            billingZip: this.postalCode.$.trim(),
            number: responseData.Number,
            name: responseData.CardholderName,
            expMonth: responseData.ExpMonth,
            expYear: responseData.ExpYear,
            cvc: responseData.Cvc,
            issuerName: this.convertToCardIssuer(responseData.IssuerName),
        };

        const request: ChargeCreditCardRequest = {
            method: card,
            amount: payment.amount,
            notificationEmail: this.clientData.loginData.email,
            invoice: invoiceId || '',
            storePaymentMethod: storePaymentMethod,
            chargeType: payment.chargeType
        };

        const isStatementCharge = payment.chargeType === ChargeType.NUMBER_1;
        const statementRequest = isStatementCharge ? request as unknown as ChargeCustomerCreditCardRequest : undefined;

        if (statementRequest) {
            statementRequest.splits = payment.splits;
            statementRequest.customer = payment.statementId;
            statementRequest.chargeType = payment.chargeType
        }

        try {
            const response = statementRequest ?
                await this.chargeApi.chargeCreditCardMultiple(statementRequest, true,
                    {
                        400: 'Failed to charge card. Verify the information you’ve entered or try a different card.',
                        500: 'Insufficient funds'
                    })
                : await this.chargeApi.chargeCreditCard(request, true,
                    {
                        400: 'Failed to charge card. Verify the information you’ve entered or try a different card.',
                        500: 'Insufficient funds'
                    });
            this.processChargeResponse(invoice, payment, response.data);
        } catch (e) {
            const errorMessage = e as string;
            this.callPostError(errorMessage);
            this.processChargeFailure(errorMessage, invoice, payment)
        } finally {
            runInAction(() => {
                this.processing = false;
            });
        }
    };

    @action
    private processPayByBankCharge = async (paymentMethod: PayByBankMethodInfo, payment: PendingInvoicePayment, invoice: Invoice) => {
        this.processing = true;
        const amount = payment.amount;
        const selectedCustomerId = this.customerSelector.value!;
        this.callPostError(undefined);

        const invoiceId = payment.chargeType === ChargeType.NUMBER_0 ? invoice.id : undefined;

        if (this.clientData.isLoggedIn && paymentMethod.paymentMethodId) {
            const request: ChargeCustomerStoredPaymentMethodRequest = {
                method: { id: paymentMethod.paymentMethodId },
                amount: amount,
                invoice: invoiceId,
                customerId: selectedCustomerId,
                customerExternalId: invoice.customerExternalId,
                notificationEmail: this.clientData.loginData.email,
                splits: payment.splits,
                chargeType: payment.chargeType,
                storePaymentMethod: false,
                isCreditCard: false,
            };
            try {
                const response = await this.payByBankApi.chargeStoredPaymentMethod(request);
                if (!response.data.transaction) {
                    // Show error if no transaction is set
                    this.callPostError(response.data.message || 'Failed to charge bank account. Verify your bank account or try a different bank account.');
                } else {
                    this.processChargeResponse(invoice, payment, response.data);
                }
            }
            catch (e) {
                runInAction(() => {
                    this.callPostError('Failed to charge bank account. Verify your bank account or try a different bank account.');
                    this.processChargeFailure(e as string, invoice, payment);
                    this.errorMessage = undefined;
                });
            }
            finally {
                runInAction(() => {
                    this.processing = false;
                });
            }
        } else {
            const selectedCustomerId = this.customerSelector.value!;
            const selectedCustomer = this.customerInfos.find(c => c.id === selectedCustomerId);
            const bankAccount: BankAccountInfo = {
                name: invoice.customerName || selectedCustomer?.name || this.clientData.loginData.customerName || '',
                routing: (paymentMethod.routingNumber || '').replace(/[^0-9]/g, ''),
                number: (paymentMethod.number || '').replace(/[^0-9]/g, ''),
                checkNumber: '',
                publicToken: paymentMethod.token,
                accountId: paymentMethod.accountId,
            };

            const request: ChargeCustomerACHRequest = {
                isBusinessPayment: paymentMethod.bankAccountType === BankAccountHolderType.Business,
                chargeType: payment.chargeType,
                method: bankAccount,
                amount: amount,
                customerId: selectedCustomerId || invoice.customerId || Math.abs(this.hashCode(invoice.id)),
                customerExternalId: invoice.customerExternalId,
                invoice: invoiceId,
                notificationEmail: this.clientData.loginData.email,
                splits: payment.splits,
                storePaymentMethod: false
            };

            try {
                const response = await this.payByBankApi.charge(request, true);

                if (!response.data.transaction) {
                    // Show error if no transaction is set
                    this.callPostError(response.data.message || 'Failed to charge bank account. Verify your bank account or try a different bank account.');
                } else {
                    this.processChargeResponse(invoice, payment, response.data);
                }
            } catch (e) {
                runInAction(() => {
                    this.callPostError('Failed to charge bank account. Verify your bank account or try a different bank account.');
                    this.processChargeFailure(e as string, invoice, payment);
                    this.errorMessage = undefined;
                });
            } finally {
                runInAction(() => {
                    this.processing = false;
                });
            }
        }
    };

    @action
    private hashCode = (s: string) => {
        return s.split("").reduce(function (a, b) {
            a = ((a << 5) - a) + b.charCodeAt(0);
            return a & a;
        }, 0);
    };

    @action
    private processChargeResponse = (invoice: Invoice, payment: PendingInvoicePayment, response: ChargeResponse) => {
        const data = response;
        const amount = payment.amount;
        runInAction(() => {
            const invoiceNumber = invoice.number || invoice.id.toString();
            const confirmation: PaymentConfirmation = {
                number: data.payment,
                amountPaid: amount,
                remainingBalance: data.remainingBalance || payment.amountRemaining,
                notificationEmail: data.notificationEmail,
                transaction: data.transaction,
                invoiceNumber: invoiceNumber,
                customerId: invoice.customerId,
            };

            // invalidate cached invoice after successful payment is made
            if (payment.invoiceNumber) {
                delete this.invoiceCache[payment.invoiceNumber];
            }

            const isStatement = payment.chargeType === ChargeType.NUMBER_1;

            if (isStatement) {
                this.addPaymentConfirmationToCache(payment.statementId!!, confirmation);
                this.router.history.replace(
                    `/statement/${payment.statementId}/confirm-payment/${confirmation.transaction}`
                );
            } else {
                this.addPaymentConfirmationToCache(confirmation.number, confirmation);

                this.analytics.paymentAttemptSuccess(
                    invoiceNumber,
                    invoice.customerId,
                    amount
                );

                this.router.history.replace(
                    `/invoice/${invoice.id}/confirm-payment/${confirmation.number}`
                );
            }
        });
    };

    @action
    private addPaymentConfirmationToCache = (confirmId: string, confirmation: PaymentConfirmation) => {
        sessionStorage.setItem(confirmId, JSON.stringify(confirmation));
        this.paymentConfirmationCache[confirmId] = confirmation;
    };

    @action
    private processChargeFailure = (error: string, invoice: Invoice, payment: PendingInvoicePayment) => {
        runInAction(() => {
            if (invoice) {
                this.analytics.paymentAttemptFail(
                    invoice.number || invoice.id.toString(),
                    invoice.customerId,
                    payment.amount,
                    error
                );
            }
        });
    };

    @action
    getCachedConfirmation = (confirmationNumber: string): PaymentConfirmation | undefined => {
        let confirmation: PaymentConfirmation | undefined = this.paymentConfirmationCache[confirmationNumber];
        if (!confirmation) {
            // Backup for cache failure
            var item = sessionStorage.getItem(confirmationNumber);
            confirmation = item ? JSON.parse(item) as PaymentConfirmation : undefined;
            sessionStorage.removeItem(confirmationNumber);
        }
        return confirmation;
    }

    @action
    processACH = async (paymentMethod: PaymentMethodInfo) => {
        const selectedCustomerId = this.customerSelector.value!;
        try {
            const bankAccountInfo: EditBankAccount = {
                id: 0,
                customerId: selectedCustomerId,
                primary: this.needToStoreAsPrimary,
                membership: true,
                businessUnitId: 0,
                customerName: paymentMethod.customerName,
                number: paymentMethod.number,
                routing: paymentMethod.routingNumber,
                zip: this.postalCode.$.trim()
            };

            const result = await this.savedBankAccountsApi.saveBankAccount(selectedCustomerId, bankAccountInfo, true,
                {
                    500: 'Failed to store bank account. Verify the information you’ve entered or try a different bank account.',
                }
            );
            this.lastSavedPaymentMethodId = result.data.id;
            await this.getSavedPaymentMethodsForCustomer(selectedCustomerId);
        } catch (e) {
            runInAction(() => {
                this.errorMessage = e;
                this.callCompleteAction(false);
            });
        } finally {
            runInAction(() => {
                this.processing = false;
                this.callCompleteAction(true);
            });
        }
    };

    @action
    processGatewayAddNewCard = async (callback: (paymentMethod: PaymentMethodInfo | undefined) => void) => {
        this.paymentGatewayService.handleSubmit(async (pgResponse: PGResponse) => {
            if (pgResponse.Type === PaymentGatewayResponseType.Error) {
                this.processing = false;
                callback(undefined);
                return;
            }

            this.lastPGResponse = pgResponse;

            const responseData = pgResponse.Data;

            const cardNumber = responseData.Number;
            const cardName = getCardNameByIssuer(this.convertToCardIssuer(responseData.IssuerName));
            const customerName = responseData.CardholderName;
            const cardNumberEnding = cardNumber.substring(cardNumber.length - 4, cardNumber.length);

            const paymentMethod: PaymentMethodInfo = {
                paymentMethodId: 0,
                primary: this.needToStoreAsPrimary,
                displayText: `${cardName} ending in *${cardNumberEnding}`,
                name: cardName,
                selected: true,
                icon: getCardIcon(cardName),
                expires: `${parseInt(responseData.ExpMonth, 10)}/${parseInt(responseData.ExpYear, 10)}`,
                customerName: customerName,
                bankName: cardName,
                number: cardNumber,
                paymentMethodType: PaymentMethodType.CreditCard
            };

            callback(paymentMethod);
        });
    }

    @action
    processAddNewCard = async (pgResponse: PGResponse) => {
        if (pgResponse.Type === PaymentGatewayResponseType.Error) {
            this.processing = false;
            this.callCompleteAction(false);
            return;
        }

        const responseData = pgResponse.Data;
        const selectedCustomerId = this.customerSelector.value!;

        const cardInfo: EditCreditCard = {
            id: 0,
            customerId: selectedCustomerId,
            primary: this.needToStoreAsPrimary,
            membership: true,
            businessUnitId: 0,
            isExpired: false,
            issuer: this.convertToCardIssuer(responseData.IssuerName),
            expYear: parseInt(responseData.ExpYear, 10),
            expMonth: parseInt(responseData.ExpMonth, 10),
            cvv: responseData.Cvc,
            number: responseData.Number,
            cardholderName: responseData.CardholderName,
            zip: this.postalCode.$.trim(),
            token: responseData.Token,
        };

        await this.processCreditCard(cardInfo);
    };

    @action
    processCreditCard = async (cardInfo: EditCreditCard) => {
        const selectedCustomerId = this.customerSelector.value!;
        try {
            const result = await this.savedCreditCardsApi.saveCreditCard(selectedCustomerId, cardInfo);
            this.lastSavedPaymentMethodId = result.data.id;
            await this.getSavedPaymentMethodsForCustomer(selectedCustomerId);
        } catch (e) {
            runInAction(() => {
                this.errorMessage = e;
                this.callCompleteAction(false);
            });
        } finally {
            runInAction(() => {
                this.processing = false;
                this.callCompleteAction(true);
            });
        }
    };

    @action
    resetErrorMessage() {
        this.errorMessage = undefined;
    };

    // Computed
    @computed get phone() {
        return this.clientData.portalData ? this.clientData.portalData.brandData.phone : undefined;
    };

    @computed get membershipName() {
        return this.membershipNameTypeStore.name || 'Membership';
    };

    @computed get availablePaymentMethodsCount() {
        return this.savedPaymentMethods?.filter(paymentMethod => !this.isExpired(paymentMethod)).length || 0;
    };
}
